import type * as React from 'react';
import { cn } from '@/lib/utils/cn';
import { ChartTooltip, useChartTooltip } from './chart-tooltip';
import {
  ChartEmpty,
  ChartLegend,
  SEVERITY_BG,
  SEVERITY_LABEL,
  SEVERITY_ORDER,
  SrTable,
  cssVars,
  plural,
  type Severity,
  type SeverityCounts,
} from './shared';
import { useEntryMotion } from './use-entry-motion';

/**
 * Open findings by severity (prototype `sevBar()`, `sevLegend()`,
 * `sevRows()`), drawn in the severity ramp (Critical darkest), segments 2px
 * apart. Severity always travels with its word: the legend, the tooltips
 * and the labels name it.
 *
 * SeverityBar: one stacked bar. Inline (domain tile, findings card), so it
 * is an image whose label reads the counts; with `onSegmentSelect` each
 * segment is a button (the findings filter). Given `total` and `scale`
 * its width is total / scale, so rows share one axis.
 *
 * SeverityByDomain: one row per domain with open findings, most first:
 * name, bar, total. Counts and totals come from the API.
 */

const HEIGHT = { sm: 'h-2.5', md: 'h-3', lg: 'h-3.5' } as const;

export interface SeverityBarProps {
  counts: SeverityCounts;
  /** Open findings in this bar, from the API (needed with `scale`). */
  total?: number;
  /** The largest total among sibling bars: this bar's width is total / scale. */
  scale?: number;
  /** Whose findings these are ("Schedule"), for tooltips and the label. */
  label?: string;
  /** sm 10px (tile), md 12px (rows), lg 14px (findings card). */
  size?: keyof typeof HEIGHT;
  onSegmentSelect?: (severity: Severity) => void;
  motionKey?: string | number;
  className?: string;
}

export function SeverityBar({
  counts,
  total,
  scale,
  label,
  size = 'sm',
  onSegmentSelect,
  motionKey,
  className,
}: SeverityBarProps) {
  const { animate, runKey } = useEntryMotion(motionKey);
  const { tip, bind } = useChartTooltip();
  const present = SEVERITY_ORDER.filter((s) => counts[s] > 0);
  const width = scale && total !== undefined ? `${(total / scale) * 100}%` : '100%';
  const where = label ? ` · ${label}` : '';
  const read = `Open findings${label ? ` in ${label}` : ''}: ${SEVERITY_ORDER.map(
    (s) => `${counts[s]} ${SEVERITY_LABEL[s]}`,
  ).join(', ')}`;

  const segment = (s: Severity) => {
    const cls = cn(
      'block h-full min-w-1 border-0 p-0 first:rounded-l-[3px] last:rounded-r-[3px]',
      SEVERITY_BG[s],
    );
    const style: React.CSSProperties = { flex: counts[s] };
    const handlers = bind({
      title: `${SEVERITY_LABEL[s]}${where}`,
      value: plural(counts[s], 'open finding'),
    });
    return onSegmentSelect ? (
      <button
        key={s}
        type="button"
        className={cn(
          cls,
          'cursor-pointer hover:brightness-[1.15] focus-visible:brightness-[1.15] focus-visible:outline-none',
        )}
        style={style}
        aria-label={`${SEVERITY_LABEL[s]}${where}: ${plural(counts[s], 'open finding')}`}
        onClick={() => onSegmentSelect(s)}
        {...handlers}
      />
    ) : (
      <i key={s} className={cls} style={style} {...handlers} />
    );
  };

  return (
    <span key={runKey} className={cn('block', animate && 'chart-anim', className)}>
      <span
        role={onSegmentSelect ? 'group' : 'img'}
        aria-label={read}
        className={cn('a-grow flex gap-0.5', HEIGHT[size])}
        style={{ width }}
      >
        {present.map(segment)}
      </span>
      <ChartTooltip tip={tip} />
    </span>
  );
}

/** All four severities with their counts, words beside the swatches. */
export function SeverityLegend({
  counts,
  className,
}: {
  counts: SeverityCounts;
  className?: string;
}) {
  return (
    <ChartLegend
      className={className}
      items={SEVERITY_ORDER.map((s) => ({
        color: SEVERITY_BG[s],
        label: (
          <span>
            <b className="font-semibold text-ink-900">{counts[s]}</b> {SEVERITY_LABEL[s]}
          </span>
        ),
      }))}
    />
  );
}

export interface SeverityRow {
  id: string;
  name: string;
  counts: SeverityCounts;
  /** Open findings in the domain, from the API. */
  total: number;
}

export interface SeverityByDomainProps {
  rows: SeverityRow[];
  /** Show the first n rows (the Overview card shows 5). */
  limit?: number;
  /** A segment click: open the findings filtered to the domain and severity. */
  onSelect?: (id: string, severity: Severity) => void;
  /** The domain name cell, e.g. a router Link; plain text by default. */
  renderName?: (row: SeverityRow) => React.ReactNode;
  motionKey?: string | number;
  className?: string;
}

export function SeverityByDomain({
  rows,
  limit,
  onSelect,
  renderName,
  motionKey,
  className,
}: SeverityByDomainProps) {
  const { animate, runKey } = useEntryMotion(motionKey);
  // Ordering only: most open findings first; domains with none left out.
  const shown = rows
    .filter((r) => r.total > 0)
    .map((r, i) => ({ r, i }))
    .sort((a, b) => b.r.total - a.r.total || a.i - b.i)
    .map(({ r }) => r)
    .slice(0, limit);

  if (!shown.length) {
    return (
      <ChartEmpty title="No open findings" className={className}>
        Findings come from interviews, document reviews and your own observations.
      </ChartEmpty>
    );
  }

  const scale = Math.max(1, ...shown.map((r) => r.total));

  return (
    <div key={runKey} className={cn('flex flex-col gap-0.5', animate && 'chart-anim', className)}>
      {shown.map((r, i) => (
        <div
          key={r.id}
          className="a-rise -mx-1.5 grid grid-cols-[170px_1fr_28px] items-center gap-2.5 rounded-[6px] px-1.5 py-[5px] text-[13px] text-ink-900 hover:bg-paper-50"
          style={cssVars({ '--i': i })}
        >
          <span className="truncate">{renderName ? renderName(r) : r.name}</span>
          <span className="block" style={cssVars({ '--i': i })}>
            <SeverityBar
              counts={r.counts}
              total={r.total}
              scale={scale}
              label={r.name}
              size="md"
              onSegmentSelect={onSelect ? (s) => onSelect(r.id, s) : undefined}
            />
          </span>
          <span className="text-right text-[12px] font-semibold tabular-nums">{r.total}</span>
        </div>
      ))}
      <SrTable
        caption="Open findings by domain"
        head={['Domain', 'Critical', 'High', 'Medium', 'Low', 'Total']}
        rows={shown.map((r) => [
          r.name,
          r.counts.critical,
          r.counts.high,
          r.counts.medium,
          r.counts.low,
          r.total,
        ])}
      />
    </div>
  );
}
